import {
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useColorMode, useToken } from '@chakra-ui/react';
import { cn } from '../../lib/utils';

type TracingBeamProps = {
  children: ReactNode;
  className?: string;
};

export function TracingBeam({ children, className }: TracingBeamProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const { colorMode } = useColorMode();
  const [beamColor, trackColor] = useToken('colors', [
    `mode.${colorMode}.accent`,
    `mode.${colorMode}.border`,
  ]);
  const shouldReduceMotion = useReducedMotion();
  const gradientId = `tracing-beam-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateHeight = () => setContentHeight(content.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 0.2', 'end 0.8'],
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 24,
    mass: 0.35,
  });
  const markerY = useTransform(
    progress,
    [0, 1],
    [0, Math.max(0, contentHeight - 14)]
  );

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1 top-0 w-6 md:left-3"
      >
        <svg
          width="24"
          height={contentHeight}
          viewBox={`0 0 24 ${contentHeight}`}
          fill="none"
          className="block"
        >
          <path
            d={`M 12 0 L 12 ${contentHeight}`}
            stroke={trackColor}
            strokeWidth="1.5"
            strokeOpacity="0.55"
          />
          <motion.path
            d={`M 12 0 L 12 ${contentHeight}`}
            stroke={`url(#${gradientId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={{ pathLength: shouldReduceMotion ? 1 : 0 }}
            style={{ pathLength: shouldReduceMotion ? 1 : progress }}
          />
          <defs>
            <linearGradient
              id={gradientId}
              x1="12"
              y1="0"
              x2="12"
              y2={contentHeight}
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor={beamColor} stopOpacity="0.15" />
              <stop offset="0.35" stopColor={beamColor} />
              <stop offset="1" stopColor={beamColor} />
            </linearGradient>
          </defs>
        </svg>

        {!shouldReduceMotion && (
          <motion.div
            className="absolute left-[5px] top-0 grid h-[14px] w-[14px] place-items-center rounded-full border"
            style={{
              y: markerY,
              borderColor: beamColor,
              backgroundColor: beamColor,
              boxShadow: `0 0 14px ${beamColor}`,
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
          </motion.div>
        )}
      </div>

      <div ref={contentRef} className="pl-12 md:pl-20">
        {children}
      </div>
    </div>
  );
}
